import { useState, useEffect, useRef } from "react";
import { DrugSearchResult, searchDrugs } from "@/lib/api";
import { UI_CONSTANTS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

interface UseDrugSearchResult {
  drugs: DrugSearchResult[];
  isLoading: boolean;
}

export function useDrugSearch(query: string): UseDrugSearchResult {
  const { currentUser } = useAuth();
  const [drugs, setDrugs] = useState<DrugSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stale-response guard: if an earlier request (e.g. "syr") resolves after a
  // later one (e.g. "syrup"), its results must not overwrite the newer ones.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query || query.trim().length < UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH) {
      requestIdRef.current++;
      setDrugs([]);
      setIsLoading(false);
      return;
    }

    const doctorId = currentUser?.doctor_id || currentUser?.id || "";
    if (!doctorId) {
      requestIdRef.current++;
      setDrugs([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      try {
        const results = await searchDrugs(query.trim(), doctorId);
        if (requestId !== requestIdRef.current) return; // stale — discard
        setDrugs(results);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setDrugs([]);
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, currentUser?.doctor_id, currentUser?.id]);

  return { drugs, isLoading };
}
